'use client';

import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import { type ReactNode, useEffect, useMemo } from 'react';
import { AgentLineScript } from '@/app/components/icons/AgentLineScript';
import { BaseLineAppearance } from '@/app/components/icons/BaseLineAppearance';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { ClawPassIcon } from '@/app/components/icons/ClawPassIcon';
import { ProductFillCredits } from '@/app/components/icons/ProductFillCredits';
import { ProductLineTimestamp } from '@/app/components/icons/ProductLineTimestamp';
import { Button } from '@/app/components/ui/button';
import { CLAW_RULES_URL, CLAW_THEMES, SITE, type ViduTheme } from '@/app/constants';
import { useClaw, useCountdown, useCredits, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ClawUserVariant } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { openInNewTab } from '@/app/utils/client';
import { useClawPassExpire } from '../../../_hook/use-claw-pass-expire';
import { useOpenPurchaseDialog } from '../../../_hook/use-open-purchase-dialog';

const USER_INFO_BG_IMAGE =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/menu-item-4f1d87b8.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/menu-item-4f1d87b8.webp';

const FROM_PAGE = 'claw_nav_user_card';

interface MenuRowProps {
  icon: ReactNode;
  label: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
}

const MenuRow = ({ icon, label, right, onClick }: MenuRowProps) => {
  const content = (
    <>
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex size-4 shrink-0 items-center justify-center text-system-white">{icon}</span>
        <span className="truncate text-sm text-system-white">{label}</span>
      </div>
      <div className="flex shrink-0 items-center">{right}</div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex h-10 w-full items-center justify-between gap-2 rounded-8 px-2 py-2 text-left transition-colors hover:bg-system-white08"
      >
        {content}
      </button>
    );
  }

  return <div className="flex h-10 w-full items-center justify-between gap-2 rounded-8 px-2 py-2">{content}</div>;
};

// 0 → 仅 "0"；< 60s → "x秒"；>= 60s → "x.x分钟"（向上取整保留一位小数）
const formatClawSeconds = (
  seconds: number,
  t: ReturnType<typeof useTranslations>,
): { value: string; unitText: string } => {
  if (seconds <= 0) return { value: '0', unitText: '' };
  if (seconds < 60) return { value: String(seconds), unitText: t('claw.nav.userCard.sec') };
  const minutes = Math.ceil((seconds / 60) * 10) / 10;
  return { value: minutes.toFixed(1), unitText: '' };
};

interface NavUserPopoverProps {
  userVariant?: ClawUserVariant;
}

export const NavUserPopover = ({ userVariant }: NavUserPopoverProps) => {
  const t = useTranslations();
  const { theme, setTheme } = useTheme();
  const { creditsInfo, clawPass } = useStore((state) => ({
    creditsInfo: state.creditsInfo,
    clawPass: state.clawPass,
  }));

  const { open: openClawPassUsageDialog } = useGlobalModal('clawPassUsageDialog');
  const { open: openCreditDetailsDialog } = useGlobalModal('creditDetailsDialog');
  const openPurchaseDialog = useOpenPurchaseDialog();

  const { getMyClaws } = useClaw();
  const { updateCredits } = useCredits();

  const isTrial = userVariant === 'trial' && !!clawPass?.valid_to;
  const validTo = clawPass?.valid_to;

  const trialTotalSeconds = useMemo(() => {
    if (!isTrial || !validTo) return 0;
    const endMs = new Date(validTo).getTime();
    if (Number.isNaN(endMs)) return 0;
    const diffMs = endMs - Date.now();
    return Math.max(0, Math.ceil(diffMs / 1000));
  }, [isTrial, validTo]);

  const showMinutes = trialTotalSeconds > 0 && trialTotalSeconds <= 3600;

  const [countdownSeconds, { startCountdown }] = useCountdown({
    intervalMs: 60,
    onFinish: () => getMyClaws(),
  });

  useEffect(() => {
    if (showMinutes) {
      startCountdown(trialTotalSeconds);
    }
  }, [showMinutes, trialTotalSeconds, startCountdown]);

  const trialTimeDisplay = useMemo(() => {
    if (!isTrial || trialTotalSeconds <= 0) return null;
    if (showMinutes) {
      const minutes = Math.ceil(countdownSeconds / 60);
      return { type: 'minutes' as const, minutes };
    }
    const hours = Math.ceil(trialTotalSeconds / 3600);
    return { type: 'hours' as const, hours };
  }, [isTrial, trialTotalSeconds, showMinutes, countdownSeconds]);

  useEffect(() => {
    reportEvent('page_view', {
      page_name: 'my_page',
      page_level: 2,
      from_page: FROM_PAGE,
    });

    // 更新 clawpass 状态 & 积分数
    getMyClaws();
    updateCredits();
  }, []);

  const handlePurchase = () => {
    openPurchaseDialog('claw_page_my_purchase');
  };

  const handleClawPassDetails = () => {
    openClawPassUsageDialog();
  };

  const handleCreditsDetails = () => {
    openCreditDetailsDialog({ fromAgentProject: FROM_PAGE });
  };

  const dailyQuotaSeconds = clawPass?.daily_quota_seconds ?? 0;
  const remainSeconds = clawPass?.remain_seconds ?? 0;
  const remainDisplay = formatClawSeconds(remainSeconds, t);

  // 配额耗尽提示（仅 clawPass 用户）：保留旧逻辑
  const consumingCreditsTips =
    clawPass?.has_benefit && userVariant === 'clawPass' && remainSeconds <= 0
      ? t('claw.nav.userCard.consumingCredits')
      : '';

  const { useHours: showExpireCountdown, days: daysRemaining, hours: hoursRemaining } = useClawPassExpire();

  const handleDoc = () => {
    openInNewTab(CLAW_RULES_URL);
  };

  return (
    <div className="flex w-[280px] flex-col gap-1 rounded-12 border border-system-white08 light:border-[rgba(0,0,0,0.08)] bg-[rgba(34,37,42,0.64)] light:bg-white p-1 light:shadow-[0_18px_56px_rgba(15,23,42,0.12)] shadow-[0_4px_20px_0_rgba(0,0,0,0.4)] backdrop-blur-[30px]">
      <div
        className="relative flex flex-col items-center gap-3 overflow-hidden light:rounded-[14px] rounded-tl-[4px] rounded-tr-[4px] light:border light:border-black/[0.08] bg-center bg-cover light:bg-[rgba(87,101,242,0.16)] bg-no-repeat px-6 py-5"
        style={{ backgroundImage: `url(${USER_INFO_BG_IMAGE})` }}
      >
        <div className="flex flex-col items-center gap-1 light:text-black text-system-white">
          {clawPass?.has_benefit && (userVariant === 'clawPass' || dailyQuotaSeconds > 0) && (
            <div className="mb-3 flex items-baseline">
              <span className="font-medium light:text-black text-[32px] text-system-white leading-none light:tracking-[-0.03em]">
                {remainDisplay.value}
              </span>
              {remainDisplay.unitText && (
                <span className="ml-0.5 light:text-black text-sm text-system-white">{remainDisplay.unitText}</span>
              )}
              <span className="light:text-black/24 text-sm text-system-white36 leading-none">
                /{(Math.ceil((dailyQuotaSeconds / 60) * 10) / 10).toFixed(1).replace(/\.0$/, '')}
                {t('claw.nav.userCard.min')}
              </span>
            </div>
          )}
          {clawPass?.has_benefit && (userVariant === 'clawPass' || (remainSeconds === 0 && dailyQuotaSeconds > 0)) && (
            <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-system-white16 light:bg-black/10 light:px-3 px-2 light:py-1 light:font-medium light:text-[15px] light:text-black text-base text-system-white">
              <ProductFillCredits className="size-4" /> {creditsInfo?.total ?? 0}
            </div>
          )}

          {(userVariant === 'free' || userVariant === 'trial') && dailyQuotaSeconds <= 0 && (
            <div className="mb-2 rounded-full bg-system-white16 light:bg-[rgba(0,0,0,0.12)] px-4 py-1 light:font-medium light:text-black text-base text-system-white leading-none">
              0 {t('claw.nav.userCard.min')}
            </div>
          )}

          {clawPass?.has_benefit && userVariant === 'clawPass' && daysRemaining > 0 && (
            <p className="whitespace-pre-wrap text-center light:text-black/40 text-system-white64 text-xs">
              {showExpireCountdown
                ? t('claw.nav.userCard.expiresInHours', { hours: Math.ceil(hoursRemaining) })
                : t('claw.nav.userCard.daysLeft', { days: daysRemaining })}
            </p>
          )}
          {consumingCreditsTips && (
            <p className="whitespace-pre-wrap text-center light:text-black/40 text-system-white64 text-xs">
              {consumingCreditsTips}
            </p>
          )}
        </div>
        {(userVariant === 'free' ||
          userVariant === 'trial' ||
          (clawPass?.has_benefit && userVariant === 'clawPass')) && (
          <Button
            onClick={handlePurchase}
            className="h-10 min-w-[98px] rounded-12 border border-system-white12 bg-white py-2 light:text-black text-sm text-system-black backdrop-blur-[2px] transition-opacity hover:opacity-90"
          >
            {userVariant === 'clawPass' ? t('payments.plans.passAdvanceButton') : t('claw.nav.userCard.purchase')}
          </Button>
        )}
      </div>

      {trialTimeDisplay !== null && (
        <MenuRow
          icon={<ProductLineTimestamp className="size-4 opacity-40" />}
          label={t('claw.nav.userCard.timeLeftForTrial')}
          right={
            <span className="light:text-black/40 text-sm text-system-white48">
              {trialTimeDisplay.type === 'hours'
                ? t('claw.nav.userCard.hoursLeft', { hours: trialTimeDisplay.hours })
                : t('claw.nav.userCard.minutesLeft', { minutes: trialTimeDisplay.minutes })}
            </span>
          }
        />
      )}

      <MenuRow
        icon={<ClawPassIcon className="size-4" />}
        label={t('claw.nav.userCard.clawPassDetails')}
        right={<BaseLineRight className="size-4 text-system-white24" />}
        onClick={handleClawPassDetails}
      />

      <MenuRow
        icon={<ProductFillCredits className="size-4" />}
        label={t('claw.nav.userCard.creditsDetails')}
        right={<BaseLineRight className="size-4 text-system-white24" />}
        onClick={handleCreditsDetails}
      />

      <div className="h-[1px] w-full bg-system-white08" />

      <MenuRow
        icon={<BaseLineAppearance className="size-4" />}
        label={t('claw.nav.userCard.appearance')}
        right={
          <div className="flex items-center rounded-8 bg-[rgba(0,0,0,0.4)] light:bg-black/10 p-0.5">
            {CLAW_THEMES.map((mode: ViduTheme) => {
              const active = theme === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setTheme(mode)}
                  className={cn(
                    'rounded-8 border border-transparent px-3 py-1 text-xs leading-[1.4] transition-colors',
                    active
                      ? 'border-system-white12 light:border-transparent bg-system-white24 light:bg-[#5765F2] light:text-white text-system-white light:shadow-[0_1px_3px_rgba(87,101,242,0.24)]'
                      : 'light:text-black/40 text-system-white light:opacity-100 opacity-40 light:hover:text-black hover:opacity-60',
                  )}
                >
                  {t(mode === 'light' ? 'claw.nav.userCard.themeLight' : 'claw.nav.userCard.themeDark')}
                </button>
              );
            })}
          </div>
        }
      />

      <MenuRow icon={<AgentLineScript className="size-4" />} label={t('claw.nav.userCard.doc')} onClick={handleDoc} />
    </div>
  );
};
